import { createTheme } from "@mui/material/styles";

/**
 * MUI 主题配置 — 对齐视觉基线 v1（design/assets/theme.css :root 令牌）。
 *
 * 映射依据 kickoff/04-前端改造策略.md §2：
 * - primary: --brand-600 #4F46E5（light --brand-500 #6366F1，自扩 50 #EEF0FE）
 * - secondary: --cyan-600 #0891B2
 * - text 阶: --ink-900/700/500/400
 * - 语义色: --p0/--p1/--ok/--info + 各浅底（semanticBg.* 供徽章使用）
 * - 圆角: --r-card 12（shape.borderRadius），按钮小组件 --r-sm 8（组件覆盖）
 *
 * 自定义 palette 扩展：
 * - brand.{50,500,600}：品牌色阶（badge 浅底 / hover / 主色）
 * - semanticBg.{error,warning,success,info,neutral}：语义浅底（徽章/提示块背景）
 * - lineSoft：--line-soft #F0F1F4（行内分隔线）
 * sx 中可直接以路径引用，如 bgcolor: "brand.50" / bgcolor: "semanticBg.error"。
 */
declare module "@mui/material/styles" {
  interface Palette {
    brand: { 50: string; 500: string; 600: string };
    semanticBg: {
      error: string;
      warning: string;
      success: string;
      info: string;
      neutral: string;
    };
    lineSoft: string;
  }
  interface PaletteOptions {
    brand?: { 50: string; 500: string; 600: string };
    semanticBg?: {
      error: string;
      warning: string;
      success: string;
      info: string;
      neutral: string;
    };
    lineSoft?: string;
  }
}

export const theme = createTheme({
  palette: {
    primary: {
      main: "#4F46E5",
      light: "#6366F1",
      dark: "#3730A3",
      contrastText: "#ffffff",
    },
    secondary: {
      main: "#0891B2",
      light: "#22D3EE",
      dark: "#155E75",
      contrastText: "#ffffff",
    },
    brand: {
      50: "#EEF0FE",
      500: "#6366F1",
      600: "#4F46E5",
    },
    error: {
      main: "#DC2626",
      light: "#EF4444",
      dark: "#B91C1C",
    },
    warning: {
      main: "#D97706",
      light: "#F59E0B",
      dark: "#B45309",
    },
    success: {
      main: "#059669",
      light: "#10B981",
      dark: "#047857",
    },
    info: {
      main: "#2563EB",
      light: "#3B82F6",
      dark: "#1D4ED8",
    },
    semanticBg: {
      error: "#FDECEC",
      warning: "#FBF3E4",
      success: "#E7F6F0",
      info: "#EAF1FE",
      neutral: "#F3F4F6",
    },
    background: {
      default: "#F6F7F9",
      paper: "#ffffff",
    },
    text: {
      primary: "#111827",
      secondary: "#6B7280",
      disabled: "#9CA3AF",
    },
    grey: {
      50: "#F9FAFB",
      100: "#F3F4F6",
      200: "#E5E7EB",
      300: "#D1D5DB",
      400: "#9CA3AF",
      500: "#6B7280",
      600: "#4B5563",
      700: "#374151",
      800: "#1F2937",
      900: "#111827",
    },
    divider: "#E5E7EB",
    lineSoft: "#F0F1F4",
  },
  shape: {
    borderRadius: 12,
  },
  typography: {
    fontFamily:
      '-apple-system, "Segoe UI", "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", sans-serif',
    fontSize: 13,
    h1: { fontSize: "20px", fontWeight: 700, color: "#111827" },
    h2: { fontSize: "19px", fontWeight: 600, color: "#111827" },
    h3: { fontSize: "17px", fontWeight: 600, color: "#111827" },
    h4: { fontSize: "14px", fontWeight: 600, color: "#111827" },
    h5: { fontSize: "13px", fontWeight: 600, color: "#111827" },
    h6: { fontSize: "12px", fontWeight: 600, color: "#111827" },
    body1: { fontSize: "13px", color: "#374151" },
    body2: { fontSize: "12px", color: "#374151" },
    caption: { fontSize: "12px", color: "#6B7280" },
    button: { textTransform: "none" as const },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        // 全局数字等宽（tabular-nums），对齐 theme.css .num
        body: {
          fontVariantNumeric: "tabular-nums",
          WebkitFontSmoothing: "antialiased",
          MozOsxFontSmoothing: "grayscale",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: ({ theme }: { theme: import("@mui/material/styles").Theme }) => ({
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 12,
          boxShadow: "0 1px 2px rgba(17,24,39,.04)",
        }),
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: 20,
          "&:last-child": { paddingBottom: 20 },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          borderRadius: 8,
          fontWeight: 500,
        },
        sizeSmall: {
          padding: "4px 10px",
          fontSize: "12px",
        },
        sizeMedium: {
          padding: "6px 14px",
          fontSize: "13px",
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: ({ theme }: { theme: import("@mui/material/styles").Theme }) => ({
          borderBottom: `1px solid ${theme.palette.lineSoft}`,
          padding: "0 16px",
          height: 48,
          fontSize: "13px",
          fontVariantNumeric: "tabular-nums",
        }),
        head: ({ theme }: { theme: import("@mui/material/styles").Theme }) => ({
          height: 36,
          backgroundColor: "#FAFAFB",
          fontSize: "12px",
          color: theme.palette.text.secondary,
          fontWeight: 600,
          letterSpacing: "0.02em",
        }),
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          "&:last-child td": { borderBottom: 0 },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontSize: "12px",
          height: "20px",
          borderRadius: "10px",
          fontWeight: 500,
        },
      },
    },
    MuiBadge: {
      styleOverrides: {
        badge: {
          fontSize: "11px",
          fontVariantNumeric: "tabular-nums",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: "none",
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        size: "small",
      },
    },
  },
});
